import { useMemo, useState } from 'react';
import { api, type Categoria } from '../api';
import { Caricamento, Campo, Dialogo, Errore, Etichetta, Scheda, Vuoto, useAvvisi, useCarico } from '../componenti/base';

type Verso = 'ENTRATA' | 'USCITA';

interface EsitoEliminazione {
  eliminata: string;
  movimentiSpostati: number;
  documentiSpostati: number;
  regoleEliminate: number;
  destinazione: string;
}

const msg = (e: unknown) => (e instanceof Error ? e.message : 'Operazione non riuscita.');

/**
 * Editor dell'albero delle categorie: creazione a qualunque profondità,
 * rinomina, riordino, spostamento sotto un altro ramo, eliminazione. Due alberi
 * separati — Entrate e Uscite — perché una categoria vive in un solo verso.
 * Le due categorie di sistema («Da identificare») si vedono ma non si toccano.
 */
export function Categorie() {
  const avvisa = useAvvisi();
  const { dati, caricamento, errore, ricarica } = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);
  const cats = useMemo(() => dati ?? [], [dati]);

  const { figliDi } = useMemo(() => {
    const figliDi = new Map<string | null, Categoria[]>();
    for (const c of cats) figliDi.set(c.padreId, [...(figliDi.get(c.padreId) ?? []), c]);
    for (const arr of figliDi.values()) arr.sort((a, b) => a.ordine - b.ordine || a.nome.localeCompare(b.nome));
    return { figliDi };
  }, [cats]);

  const figli = (id: string | null) => figliDi.get(id) ?? [];
  const radiciDi = (verso: Verso) => figli(null).filter((c) => c.verso === verso);

  const discendenti = (id: string): Set<string> => {
    const s = new Set<string>();
    const vai = (x: string) => {
      for (const c of figli(x)) {
        s.add(c.id);
        vai(c.id);
      }
    };
    vai(id);
    return s;
  };

  // ── Stato dell'interazione ────────────────────────────────────────────────
  const [aggiungiA, setAggiungiA] = useState<string | null>(null); // id padre, o 'root:ENTRATA' / 'root:USCITA'
  const [bozza, setBozza] = useState('');
  const [rinominaId, setRinominaId] = useState<string | null>(null);
  const [rinominaVal, setRinominaVal] = useState('');
  const [sposta, setSposta] = useState<Categoria | null>(null);
  const [elimina, setElimina] = useState<Categoria | null>(null);
  const [occupato, setOccupato] = useState(false);

  const azzera = () => {
    setAggiungiA(null);
    setBozza('');
    setRinominaId(null);
    setRinominaVal('');
  };

  // ── Azioni ────────────────────────────────────────────────────────────────
  const crea = async (verso: Verso, padreId: string | null) => {
    const nome = bozza.trim();
    if (!nome) return;
    setOccupato(true);
    try {
      await api.post('/api/categorie', { nome, verso, padreId });
      avvisa('Categoria creata.');
      azzera();
      ricarica();
    } catch (e) {
      avvisa(msg(e), 'errore');
    } finally {
      setOccupato(false);
    }
  };

  const rinomina = async (id: string) => {
    const nome = rinominaVal.trim();
    if (!nome) return;
    setOccupato(true);
    try {
      await api.put(`/api/categorie/${id}`, { nome });
      avvisa('Categoria rinominata.');
      azzera();
      ricarica();
    } catch (e) {
      avvisa(msg(e), 'errore');
    } finally {
      setOccupato(false);
    }
  };

  const riordina = async (nodo: Categoria, dir: 'su' | 'giu') => {
    const fratelli = figli(nodo.padreId).filter((c) => c.verso === nodo.verso);
    const i = fratelli.findIndex((c) => c.id === nodo.id);
    const j = dir === 'su' ? i - 1 : i + 1;
    if (j < 0 || j >= fratelli.length) return;
    const ordinati = [...fratelli];
    [ordinati[i], ordinati[j]] = [ordinati[j], ordinati[i]];
    setOccupato(true);
    try {
      await Promise.all(
        ordinati.map((c, idx) => (c.ordine === idx ? null : api.put(`/api/categorie/${c.id}`, { ordine: idx }))),
      );
      ricarica();
    } catch (e) {
      avvisa(msg(e), 'errore');
    } finally {
      setOccupato(false);
    }
  };

  const confermaSposta = async (nuovoPadre: string | null) => {
    if (!sposta) return;
    setOccupato(true);
    try {
      await api.put(`/api/categorie/${sposta.id}`, { padreId: nuovoPadre });
      avvisa('Categoria spostata.');
      setSposta(null);
      ricarica();
    } catch (e) {
      avvisa(msg(e), 'errore');
    } finally {
      setOccupato(false);
    }
  };

  const confermaElimina = async () => {
    if (!elimina) return;
    setOccupato(true);
    try {
      const r = await api.del<EsitoEliminazione>(`/api/categorie/${elimina.id}`);
      const spostati = r.movimentiSpostati + r.documentiSpostati;
      const dettaglio = [
        spostati > 0 ? `${spostati} tra movimenti e documenti spostati alla ${r.destinazione}` : '',
        r.regoleEliminate > 0 ? `${r.regoleEliminate} regole rimosse` : '',
      ]
        .filter(Boolean)
        .join(', ');
      avvisa(`«${r.eliminata}» eliminata${dettaglio ? `: ${dettaglio}` : ''}.`);
      setElimina(null);
      ricarica();
    } catch (e) {
      avvisa(msg(e), 'errore');
    } finally {
      setOccupato(false);
    }
  };

  // ── Rendering di un nodo (ricorsivo) ──────────────────────────────────────
  const renderNodo = (c: Categoria, livello: number) => {
    const suoiFigli = figli(c.id);
    const fratelli = figli(c.padreId).filter((x) => x.verso === c.verso);
    const i = fratelli.findIndex((x) => x.id === c.id);
    const inRinomina = rinominaId === c.id;
    const inAggiunta = aggiungiA === c.id;

    return (
      <div key={c.id}>
        <div
          className="group flex items-center gap-2 border-b border-inchiostro-100 py-1.5 pr-2 hover:bg-inchiostro-50"
          style={{ paddingLeft: `${12 + livello * 22}px` }}
        >
          {inRinomina ? (
            <form
              className="flex flex-1 items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                rinomina(c.id);
              }}
            >
              <input
                className="campo max-w-xs"
                autoFocus
                value={rinominaVal}
                onChange={(e) => setRinominaVal(e.target.value)}
              />
              <button type="submit" className="bottone-primario" disabled={occupato}>
                Salva
              </button>
              <button type="button" className="bottone-secondario" onClick={azzera}>
                Annulla
              </button>
            </form>
          ) : (
            <>
              <span className="flex-1 text-sm">
                {c.nome}
                {c.sistema && (
                  <span className="ml-2">
                    <Etichetta testo="🔒 di sistema" tono="neutro" titolo="Raccoglie ciò che non è ancora stato classificato: non si rinomina, non si sposta, non si elimina." />
                  </span>
                )}
              </span>
              {!c.sistema && (
                <div className="flex items-center gap-1 text-xs text-inchiostro-500 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <button
                    className="rounded px-2 py-1 hover:bg-inchiostro-200 hover:text-inchiostro-900"
                    onClick={() => {
                      azzera();
                      setAggiungiA(c.id);
                    }}
                  >
                    + sotto
                  </button>
                  <button
                    className="rounded px-2 py-1 hover:bg-inchiostro-200 hover:text-inchiostro-900"
                    onClick={() => {
                      azzera();
                      setRinominaId(c.id);
                      setRinominaVal(c.nome);
                    }}
                  >
                    Rinomina
                  </button>
                  <button
                    className="rounded px-1.5 py-1 hover:bg-inchiostro-200 hover:text-inchiostro-900 disabled:opacity-30"
                    aria-label="Sposta su"
                    disabled={i <= 0 || occupato}
                    onClick={() => riordina(c, 'su')}
                  >
                    ↑
                  </button>
                  <button
                    className="rounded px-1.5 py-1 hover:bg-inchiostro-200 hover:text-inchiostro-900 disabled:opacity-30"
                    aria-label="Sposta giù"
                    disabled={i >= fratelli.length - 1 || occupato}
                    onClick={() => riordina(c, 'giu')}
                  >
                    ↓
                  </button>
                  <button
                    className="rounded px-2 py-1 hover:bg-inchiostro-200 hover:text-inchiostro-900"
                    onClick={() => setSposta(c)}
                  >
                    Sposta in…
                  </button>
                  <button
                    className="rounded px-2 py-1 text-allerta hover:bg-allerta-chiaro"
                    onClick={() => setElimina(c)}
                  >
                    Elimina
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {inAggiunta && (
          <div style={{ paddingLeft: `${12 + (livello + 1) * 22}px` }} className="border-b border-inchiostro-100 py-2">
            <FormNuova
              etichetta={`Nuova sottocategoria di «${c.nome}»`}
              valore={bozza}
              onCambia={setBozza}
              onConferma={() => crea(c.verso, c.id)}
              onAnnulla={azzera}
              occupato={occupato}
            />
          </div>
        )}

        {suoiFigli.map((f) => renderNodo(f, livello + 1))}
      </div>
    );
  };

  const renderAlbero = (verso: Verso, titolo: string) => {
    const radici = radiciDi(verso);
    const chiaveRoot = `root:${verso}`;
    return (
      <Scheda titolo={titolo} senzaPadding>
        {radici.length === 0 && aggiungiA !== chiaveRoot ? (
          <Vuoto titolo="Nessuna categoria" testo="Creane la prima qui sotto." />
        ) : (
          <div>{radici.map((c) => renderNodo(c, 0))}</div>
        )}

        <div className="border-t border-inchiostro-200 p-3">
          {aggiungiA === chiaveRoot ? (
            <FormNuova
              etichetta={`Nuova categoria principale (${titolo.toLowerCase()})`}
              valore={bozza}
              onCambia={setBozza}
              onConferma={() => crea(verso, null)}
              onAnnulla={azzera}
              occupato={occupato}
            />
          ) : (
            <button
              className="bottone-secondario"
              onClick={() => {
                azzera();
                setAggiungiA(chiaveRoot);
              }}
            >
              + Nuova categoria principale
            </button>
          )}
        </div>
      </Scheda>
    );
  };

  return (
    <div className="pagina space-y-5">
      <header>
        <h1 className="text-lg font-semibold tracking-tight">Categorie</h1>
        <p className="text-sm text-inchiostro-500">
          Le voci in cui classificare movimenti e fatture. Sono tue: creale, rinominale, spostale e organizzale ad albero
          come preferisci. Eliminando una categoria, ciò che conteneva sale alla categoria padre — non sparisce dai
          totali.
        </p>
      </header>

      {caricamento ? (
        <Caricamento />
      ) : errore ? (
        <Errore testo={errore} riprova={ricarica} />
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {renderAlbero('ENTRATA', 'Entrate')}
          {renderAlbero('USCITA', 'Uscite')}
        </div>
      )}

      {/* Dialogo «Sposta in…» */}
      {sposta && (
        <DialogoSposta
          nodo={sposta}
          figli={figli}
          discendenti={discendenti}
          occupato={occupato}
          onConferma={confermaSposta}
          onChiudi={() => setSposta(null)}
        />
      )}

      {/* Conferma eliminazione */}
      <Dialogo aperto={!!elimina} titolo="Eliminare la categoria?" onChiudi={() => setElimina(null)} larghezza="max-w-lg">
        {elimina && (
          <div className="space-y-4">
            <p className="text-sm">
              Stai per eliminare <strong>{elimina.nome}</strong>.
              {figli(elimina.id).length > 0 ? (
                <span className="mt-2 block text-allerta">
                  Ha delle sottocategorie: spostale o eliminale prima. Un ramo intero non si cancella con un clic.
                </span>
              ) : (
                <span className="mt-2 block text-inchiostro-600">
                  I movimenti e i documenti che vi erano classificati passeranno alla{' '}
                  {elimina.padreId ? 'categoria padre' : 'voce «senza categoria»'}, e le regole che la usavano verranno
                  rimosse. Nulla sparisce dai totali.
                </span>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button className="bottone-secondario" onClick={() => setElimina(null)}>
                Annulla
              </button>
              <button
                className="bottone-pericolo"
                disabled={occupato || figli(elimina.id).length > 0}
                onClick={confermaElimina}
              >
                Elimina
              </button>
            </div>
          </div>
        )}
      </Dialogo>
    </div>
  );
}

function FormNuova({
  etichetta,
  valore,
  onCambia,
  onConferma,
  onAnnulla,
  occupato,
}: {
  etichetta: string;
  valore: string;
  onCambia: (v: string) => void;
  onConferma: () => void;
  onAnnulla: () => void;
  occupato: boolean;
}) {
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onConferma();
      }}
    >
      <Campo etichetta={etichetta} larghezza="w-72">
        <input className="campo" autoFocus placeholder="Nome della categoria" value={valore} onChange={(e) => onCambia(e.target.value)} />
      </Campo>
      <button type="submit" className="bottone-primario" disabled={occupato || !valore.trim()}>
        Crea
      </button>
      <button type="button" className="bottone-secondario" onClick={onAnnulla}>
        Annulla
      </button>
    </form>
  );
}

function DialogoSposta({
  nodo,
  figli,
  discendenti,
  occupato,
  onConferma,
  onChiudi,
}: {
  nodo: Categoria;
  figli: (id: string | null) => Categoria[];
  discendenti: (id: string) => Set<string>;
  occupato: boolean;
  onConferma: (padreId: string | null) => void;
  onChiudi: () => void;
}) {
  const [scelto, setScelto] = useState<string>(nodo.padreId ?? '__root__');
  const vietati = useMemo(() => {
    const s = discendenti(nodo.id);
    s.add(nodo.id);
    return s;
  }, [nodo, discendenti]);

  // Elenco piatto (con rientro) delle destinazioni possibili dello stesso verso.
  const opzioni = useMemo(() => {
    const out: { c: Categoria; livello: number }[] = [];
    const scendi = (padre: string | null, livello: number) => {
      for (const c of figli(padre).filter((x) => x.verso === nodo.verso)) {
        out.push({ c, livello });
        scendi(c.id, livello + 1);
      }
    };
    scendi(null, 0);
    return out;
  }, [figli, nodo.verso]);

  return (
    <Dialogo aperto titolo={`Sposta «${nodo.nome}»`} onChiudi={onChiudi} larghezza="max-w-lg">
      <div className="space-y-4">
        <Campo etichetta="Nuova posizione">
          <select className="campo" value={scelto} onChange={(e) => setScelto(e.target.value)}>
            <option value="__root__">— Livello principale —</option>
            {opzioni.map(({ c, livello }) => (
              <option key={c.id} value={c.id} disabled={vietati.has(c.id) || c.sistema}>
                {'   '.repeat(livello)}
                {c.nome}
                {c.sistema ? ' (di sistema)' : ''}
              </option>
            ))}
          </select>
        </Campo>
        <p className="text-xs text-inchiostro-500">
          Puoi spostarla al livello principale o dentro un’altra categoria delle {nodo.verso === 'ENTRATA' ? 'entrate' : 'uscite'}. Non
          dentro sé stessa o una sua sottocategoria.
        </p>
        <div className="flex justify-end gap-2">
          <button className="bottone-secondario" onClick={onChiudi}>
            Annulla
          </button>
          <button
            className="bottone-primario"
            disabled={occupato}
            onClick={() => onConferma(scelto === '__root__' ? null : scelto)}
          >
            Sposta
          </button>
        </div>
      </div>
    </Dialogo>
  );
}
